import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * "Notas existentes sobre esta pessoa", for screens whose real job is something else.
 *
 * The operator needs to KNOW a note exists before letting someone in; they do not need every note
 * transcribed into the middle of the form. So: the most recent one, a count, and a button for the
 * rest. Concluding happens in that modal, where there is room to ask for the reason the API
 * requires — the previous one-click button sent none and the backend rejected every attempt.
 */

export interface PersonNote {
  id: string;
  content: string;
  created_by_name?: string;
  created_at?: string;
}

export interface PersonNotesAlertProps {
  notes: PersonNote[];
  /** Must reject on failure — the prompt stays open and shows the message. */
  onConclude: (noteId: string, reason: string) => Promise<void>;
  darkMode?: boolean;
  className?: string;
}

const formatWhen = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR');
};

const NoteMeta: React.FC<{ note: PersonNote; darkMode?: boolean }> = ({ note, darkMode }) => {
  const when = formatWhen(note.created_at);
  if (!note.created_by_name && !when) return null;
  return (
    <p className={cn('text-[11px] mt-0.5', darkMode ? 'text-amber-400/80' : 'text-amber-700')}>
      {note.created_by_name && <span className="font-semibold">{note.created_by_name}</span>}
      {note.created_by_name && when && ' • '}
      {when}
    </p>
  );
};

export const PersonNotesAlert: React.FC<PersonNotesAlertProps> = ({
  notes,
  onConclude,
  darkMode,
  className,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [concludingId, setConcludingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!notes || notes.length === 0) return null;

  const latest = notes[0];
  const trimmedReason = reason.trim();

  const startConclude = (noteId: string) => {
    setConcludingId(noteId);
    setReason('');
    setError(null);
  };

  const cancelConclude = () => {
    setConcludingId(null);
    setReason('');
    setError(null);
  };

  const confirmConclude = async () => {
    if (!concludingId || !trimmedReason || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConclude(concludingId, trimmedReason);
      setConcludingId(null);
      setReason('');
    } catch (err: any) {
      // Left open with the text intact — retyping the reason after a failed request is how people
      // give up on a feature.
      setError(err?.message || 'Erro ao concluir nota');
    } finally {
      setSubmitting(false);
    }
  };

  const concludeButton = (noteId: string) => (
    <button
      type="button"
      onClick={() => startConclude(noteId)}
      className={cn(
        'text-[11px] font-semibold px-2 py-1 rounded border transition-colors flex-shrink-0',
        darkMode
          ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
          : 'border-gray-300 text-gray-600 hover:bg-gray-100',
      )}
    >
      Concluir
    </button>
  );

  const reasonPrompt = (
    <div className={cn('mt-2 rounded-lg border p-2', darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50')}>
      <label
        htmlFor="conclude-note-reason"
        className={cn('block text-[11px] font-semibold mb-1', darkMode ? 'text-gray-300' : 'text-gray-700')}
      >
        Motivo da conclusão *
      </label>
      <textarea
        id="conclude-note-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="Por que esta nota pode ser encerrada?"
        className={cn(
          'w-full rounded-md text-xs px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500',
          darkMode ? 'bg-gray-800 border border-gray-600 text-white' : 'border border-gray-300',
        )}
      />
      {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
      <div className="flex justify-end gap-2 mt-1.5">
        <button
          type="button"
          onClick={cancelConclude}
          className={cn('text-[11px] px-2 py-1 rounded', darkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-200')}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={confirmConclude}
          disabled={!trimmedReason || submitting}
          className={cn(
            'text-[11px] font-semibold px-2.5 py-1 rounded text-white transition-colors',
            !trimmedReason || submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700',
          )}
        >
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirmar'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div
        className={cn(
          'col-span-2 rounded-lg border px-3 py-2',
          darkMode ? 'bg-amber-900/25 border-amber-600/60' : 'bg-amber-50 border-amber-300',
          className,
        )}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className={cn('text-[11px] font-bold uppercase tracking-wider', darkMode ? 'text-amber-400' : 'text-amber-800')}>
            {notes.length === 1 ? 'Nota sobre esta pessoa' : `${notes.length} notas sobre esta pessoa`}
          </span>
          {notes.length > 1 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className={cn(
                'ml-auto text-[11px] font-semibold px-2 py-0.5 rounded border transition-colors',
                darkMode
                  ? 'border-amber-600/60 text-amber-300 hover:bg-amber-900/40'
                  : 'border-amber-400 text-amber-800 hover:bg-amber-100',
              )}
            >
              Ver todas ({notes.length})
            </button>
          )}
        </div>

        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className={cn('text-xs leading-snug', darkMode ? 'text-amber-100' : 'text-gray-900')}>
              {latest.content}
            </p>
            <NoteMeta note={latest} darkMode={darkMode} />
          </div>
          {concludeButton(latest.id)}
        </div>

        {concludingId === latest.id && !showAll && reasonPrompt}
      </div>

      {showAll && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 10000 }}
          onClick={() => setShowAll(false)}
        >
          <div
            role="dialog"
            aria-label="Notas sobre esta pessoa"
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'w-full max-w-lg max-h-[80vh] flex flex-col rounded-xl border shadow-2xl',
              darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200',
            )}
          >
            <div className={cn('flex items-center justify-between px-4 py-3 border-b', darkMode ? 'border-gray-800' : 'border-gray-200')}>
              <h3 className={cn('text-sm font-semibold', darkMode ? 'text-white' : 'text-gray-900')}>
                Notas sobre esta pessoa ({notes.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowAll(false)}
                aria-label="Fechar"
                className={cn('text-xs px-2 py-1 rounded', darkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100')}
              >
                Fechar
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {notes.map((n) => (
                <div
                  key={n.id}
                  className={cn(
                    'rounded-lg border px-3 py-2',
                    darkMode ? 'bg-gray-950 border-gray-800' : 'bg-white border-gray-200',
                  )}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-xs leading-snug whitespace-pre-wrap', darkMode ? 'text-gray-100' : 'text-gray-900')}>
                        {n.content}
                      </p>
                      <NoteMeta note={n} darkMode={darkMode} />
                    </div>
                    {concludeButton(n.id)}
                  </div>
                  {concludingId === n.id && reasonPrompt}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PersonNotesAlert;
